import React, {
  ReactNode,
  useLayoutEffect,
  useRef,
  ChangeEvent,
  useState,
} from 'react';
import { InputTextarea } from 'primereact/inputtextarea';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import styles from './InputTextArea.module.scss';

interface InputTextAreaProps {
  id: string;
  label?: string;
  name: string;
  value: string;
  placeholder?: string;
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  error?: string;
  help?: string;
  className?: string;
  inputClassName?: string;
  readOnly?: boolean;
  disabled?: boolean;
  required?: boolean;
  children?: ReactNode;
  iconLabel?: IconProp;
  longErrorMessage?: boolean;
}

const InputTextArea: React.FC<InputTextAreaProps> = ({
  id,
  label,
  name,
  value,
  placeholder,
  onChange,
  error,
  help,
  className,
  readOnly,
  disabled,
  iconLabel,
  children,
  required,
  longErrorMessage,
  inputClassName,
}) => {
  const errorMessageRef = useRef<HTMLParagraphElement>(null);
  const [errorOffset, setErrorOffset] = useState<number>(0);

  useLayoutEffect(() => {
    const updateErrorOffset = () => {
      if (errorMessageRef.current) {
        const height = errorMessageRef.current.clientHeight + 4;
        setErrorOffset(height);
      }
    };

    updateErrorOffset();

    window.addEventListener('resize', updateErrorOffset);

    return () => window.removeEventListener('resize', updateErrorOffset);
  }, [error]);

  return (
    <div className={classNames(styles.fieldContainer, className)}>
      <label
        htmlFor={id}
        className={classNames(styles.label, error && styles.errorLabel)}
      >
        {iconLabel && <FontAwesomeIcon icon={iconLabel} />} <span>{label}</span>
        {required && ' *'}
      </label>
      <div className={classNames(children && styles.innerInputDiv)}>
        <InputTextarea
          id={id}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
          name={name}
          value={value}
          onChange={onChange}
          className={classNames(
            styles.field,
            error && styles.invalid,
            inputClassName
          )}
          placeholder={placeholder}
          readOnly={readOnly}
          disabled={disabled}
        />
        {children}
        {error ? (
          <p
            ref={errorMessageRef}
            style={longErrorMessage ? { bottom: `-${errorOffset}px` } : {}}
            className={classNames(
              styles.errorMessage,
              longErrorMessage && styles.longErrorMessage
            )}
          >
            {error}
          </p>
        ) : null}
        {help ? <p className={styles.help}>{help}</p> : null}
      </div>
    </div>
  );
};

export default InputTextArea;
